import React from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { CHART_COLORS, SERIES_PALETTE, axisTick } from './chartTheme';
import { chartTooltipFormatter, chartTooltipProps } from './ChartTooltip';
import i18n from '../../i18n';

/**
 * Multi-series trend line.
 * data: [{ name|label, ...keys }]
 * series: [{ dataKey, name, color? }]
 */
export default function TrendLine({
  data = [],
  series = [{ dataKey: 'value', name: i18n.t('charts.defaultSeries', { defaultValue: 'Value' }), color: CHART_COLORS.primary }],
  xKey = 'name',
  height = 220,
  showLegend = true,
  yFormatter,
}) {
  if (!data?.length || !series?.length) return null;

  // Sample tick labels so currency (e.g. "4,00 €") never wraps onto two lines.
  const sampleVals = data.flatMap((row) => series.map((s) => Number(row[s.dataKey]) || 0));
  const maxAbs = sampleVals.length ? Math.max(...sampleVals.map((v) => Math.abs(v))) : 0;
  const sampleLabel = yFormatter ? String(yFormatter(maxAbs || 0)) : String(Math.round(maxAbs || 0));
  const yWidth = Math.min(110, Math.max(56, 16 + sampleLabel.length * 8));

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 8, right: 12, left: 8, bottom: showLegend && series.length > 1 ? 4 : 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
        <XAxis dataKey={xKey} tick={axisTick} axisLine={false} tickLine={false} />
        <YAxis
          tick={{ ...axisTick }}
          axisLine={false}
          tickLine={false}
          width={yWidth}
          tickFormatter={(v) => {
            if (!yFormatter) return v;
            // Non-breaking spaces keep "4,00 €" on one line inside the axis slot.
            return String(yFormatter(v)).replace(/\s/g, '\u00A0');
          }}
        />
        <Tooltip
          {...chartTooltipProps}
          formatter={chartTooltipFormatter(yFormatter)}
        />
        {showLegend && series.length > 1 ? (
          <Legend wrapperStyle={{ fontSize: 12, paddingTop: 4 }} />
        ) : null}
        {series.map((s, i) => (
          <Line
            key={s.dataKey}
            type="monotone"
            dataKey={s.dataKey}
            name={s.name || s.dataKey}
            stroke={s.color || SERIES_PALETTE[i % SERIES_PALETTE.length]}
            strokeWidth={2.25}
            dot={data.length <= 14}
            activeDot={{ r: 5 }}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}
